'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

interface BrandLogoProps {
  /** 'light' = logo for dark backgrounds (admin), 'dark' = logo for light backgrounds (member) */
  variant?: 'light' | 'dark';
  size?: number;
  className?: string;
}

const FALLBACK = '/assets/images/app_logo.png';

// Module-level cache so logo is only fetched once per session (survives re-renders and navigation)
const logoCache: Record<string, string> = {};
let fetchPromises: Record<string, Promise<string>> = {};

function fetchLogoUrl(variant: 'light' | 'dark'): Promise<string> {
  const key = variant === 'light' ? 'logo_light_url' : 'logo_dark_url';
  const cacheKey = `brand_logo_${variant}`;

  if (logoCache[cacheKey]) {
    return Promise.resolve(logoCache[cacheKey]);
  }

  if (fetchPromises[cacheKey]) {
    return fetchPromises[cacheKey];
  }

  fetchPromises[cacheKey] = createClient()
    .from('site_config')
    .select('value')
    .eq('key', key)
    .single()
    .then(({ data }) => {
      const url = data?.value?.trim();
      const resolved = url && url !== '' ? url : FALLBACK;
      logoCache[cacheKey] = resolved;
      return resolved;
    })
    .catch(() => {
      logoCache[cacheKey] = FALLBACK;
      return FALLBACK;
    });

  return fetchPromises[cacheKey];
}

// Pre-warm both variants immediately when this module loads
if (typeof window !== 'undefined') {
  fetchLogoUrl('dark');
  fetchLogoUrl('light');
}

export default function BrandLogo({ variant = 'dark', size = 80, className = '' }: BrandLogoProps) {
  const cacheKey = `brand_logo_${variant}`;
  // Initialize from cache synchronously to avoid any flash
  const [src, setSrc] = useState<string>(() => logoCache[cacheKey] || '');
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (logoCache[cacheKey]) {
      setSrc(logoCache[cacheKey]);
      return;
    }
    fetchLogoUrl(variant).then((url) => {
      setSrc(url);
    });
  }, [variant, cacheKey]);

  if (!src) {
    // Render a transparent placeholder while loading — same dimensions to prevent layout shift
    return (
      <div
        style={{ width: size, height: Math.round(size * 0.4), flexShrink: 0 }}
        className={className}
      />
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={errored ? FALLBACK : src}
      alt="Logo"
      width={size}
      height={size}
      style={{ width: size, height: 'auto', maxHeight: size, objectFit: 'contain', flexShrink: 0 }}
      className={className}
      onError={() => {
        setErrored(true);
        logoCache[cacheKey] = FALLBACK;
      }}
      fetchPriority="high"
      decoding="async"
    />
  );
}
